import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Plus } from "lucide-react";
import { useState } from "react";
import { CaseParticipantType } from "../dtos";
import { useAddParticipant } from "../hooks/use-participants.hook";
import { primaryButtonSx, secondaryButtonSx } from "./field-shell";

type AddType = Extract<CaseParticipantType, "JOINT" | "GUARANTOR">;

const TYPE_LABEL: Record<AddType, string> = {
  JOINT: "Add joint client",
  GUARANTOR: "Add guarantor",
};

/**
 * Self-contained add-co-applicant entry point (button + add-by-mobile dialog), placed where
 * the flow calls for it: "Add guarantor" floats in the Loan process header, "Add joint client"
 * renders inside the participant-capture step (Client Profile) — mirroring workflow-manager's
 * own uiFlags.participantCapture placement. Adding is by mobile number: the engine wakes the
 * matching pre-seeded step block, and §PhoneLinking links the app account on signup.
 */
const AddParticipantButton = ({
  caseUid,
  type,
}: {
  caseUid: string | null;
  type: AddType;
}) => {
  const theme = useTheme();
  const add = useAddParticipant(caseUid);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  const closeDialog = () => {
    setOpen(false);
    setForm({ name: "", phone: "", email: "" });
  };

  const submit = () => {
    if (!form.phone.trim()) return;
    add.mutate(
      {
        type,
        customer: {
          name: form.name.trim() || undefined,
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
        },
      },
      { onSuccess: closeDialog }
    );
  };

  return (
    <>
      {type === "GUARANTOR" ? (
        // design (add-participant spec): the header entry is a 32×32 icon button,
        // r8 with the standard input border — not a text button
        <Tooltip title={TYPE_LABEL[type]}>
          <IconButton
            aria-label={TYPE_LABEL[type]}
            onClick={() => setOpen(true)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: theme.nawy.radius.lg,
              border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
              color: theme.nawy.colors.semantic.text.accent,
            }}
          >
            <Plus size={16} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Plus size={14} />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            color: theme.nawy.colors.semantic.text.accent,
            borderColor: theme.nawy.colors.semantic.border.primary,
          }}
        >
          {TYPE_LABEL[type]}
        </Button>
      )}

      {/* §MobileQA: was a raw MUI <Dialog> — a centred desktop dialog at 390px while every other
          modal in the tab is a bottom sheet. ResponsiveModal picks the right one per breakpoint. */}
      <ResponsiveModal open={open} onClose={closeDialog} title={TYPE_LABEL[type]}>
        <Box
          sx={{
            width: { mobile: "100%", desktop: 440 },
            maxWidth: "100%",
            px: 2.5,
            py: 2,
          }}
        >
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: theme.nawy.radius.xl } }}
            />
            <TextField
              label="Mobile number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              size="small"
              required
              helperText="Required — links them to their app account on signup"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: theme.nawy.radius.xl } }}
            />
            <TextField
              label="Email (optional)"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: theme.nawy.radius.xl } }}
            />
          </Stack>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{
            px: 2.5,
            py: 2,
            borderTop: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          }}
        >
          <Button onClick={closeDialog} sx={secondaryButtonSx(theme)}>
            Cancel
          </Button>
          {/* §MobileQA: `contained` with no palette override was MUI's default #1976d2 */}
          <Button
            variant="contained"
            disableElevation
            disabled={!form.phone.trim() || add.isLoading}
            onClick={submit}
            startIcon={
              add.isLoading ? <CircularProgress size={14} /> : undefined
            }
            sx={primaryButtonSx(theme)}
          >
            Add
          </Button>
        </Stack>
      </ResponsiveModal>
    </>
  );
};

export default AddParticipantButton;
